import React, { useState } from "react";
import {
    AlertTriangle,
    Bot,
    Check,
    Copy,
    Info,
    ThumbsDown,
    ThumbsUp,
    User,
} from "lucide-react";

import { cn } from "../../utils/tailwindUtils";
import CopilotDataCards from "./CopilotDataCards";
import CopilotMarkdown from "./CopilotMarkdown";
import { getDomainMeta } from "./constants";

/**
 * One turn in the copilot thread.
 *
 * `compact` renders the widget variant (smaller type, 2-column metric tiles,
 * icon-only feedback); the default renders the full-page variant.
 */
const CopilotMessage = ({ message, compact = false, feedback, onRate }) => {
    const [copied, setCopied] = useState(false);
    const isUser = message.sender === "user";
    const domainMeta = getDomainMeta(message.domain);
    const DomainIcon = domainMeta.icon;

    const handleCopy = () => {
        // clipboard is unavailable on insecure origins — fail quietly, the
        // answer is still selectable.
        navigator.clipboard?.writeText(message.text || "").then(
            () => {
                setCopied(true);
                setTimeout(() => setCopied(false), 1800);
            },
            () => { }
        );
    };

    const avatar = (
        <div
            className={cn(
                "flex shrink-0 items-center justify-center rounded-lg",
                compact ? "h-7 w-7" : "h-9 w-9",
                isUser
                    ? "border border-border bg-card text-sidebar-primary"
                    : "bg-sidebar text-white shadow-sm"
            )}
        >
            {isUser ? (
                <User className={compact ? "h-3.5 w-3.5" : "h-4 w-4"} />
            ) : (
                <Bot className={compact ? "h-3.5 w-3.5" : "h-4 w-4"} />
            )}
        </div>
    );

    return (
        <div className={cn("flex flex-col", isUser ? "items-end" : "items-start")}>
            <div
                className={cn(
                    "flex max-w-full items-start gap-2",
                    compact ? "sm:max-w-[92%]" : "sm:max-w-[88%]",
                    isUser && "flex-row-reverse"
                )}
            >
                {avatar}

                <div
                    className={cn(
                        "min-w-0 rounded-xl px-3.5 py-2.5",
                        compact ? "text-xs" : "text-[13px]",
                        isUser
                            ? "rounded-br-sm bg-sidebar text-white"
                            : message.isError
                                ? "rounded-bl-sm border border-destructive/30 bg-destructive/10 text-foreground"
                                : "rounded-bl-sm border border-border bg-muted/50 text-foreground"
                    )}
                >
                    {/* AI header: domain badge, source, copy */}
                    {!isUser && !message.isError && (
                        <div className="mb-2 flex items-center justify-between gap-2">
                            <span
                                className={cn(
                                    "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold",
                                    domainMeta.tint
                                )}
                            >
                                <DomainIcon className="h-3 w-3" />
                                {domainMeta.label}
                            </span>

                            <div className="flex items-center gap-2">
                                {/* Tells the user whether the wording came from the model
                                    or the deterministic HRMS fallback. */}
                                {message.isLlm === false && (
                                    <span
                                        className="text-[10px] text-muted-foreground"
                                        title="Answered directly from HRMS data (AI wording unavailable)"
                                    >
                                        HRMS data
                                    </span>
                                )}
                                <button
                                    type="button"
                                    onClick={handleCopy}
                                    title="Copy answer"
                                    className="text-muted-foreground transition-colors hover:text-sidebar-primary"
                                >
                                    {copied ? (
                                        <Check className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                                    ) : (
                                        <Copy className="h-3.5 w-3.5" />
                                    )}
                                </button>
                            </div>
                        </div>
                    )}

                    {message.isError ? (
                        <p className="flex items-start gap-2 leading-relaxed">
                            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" />
                            <span>{message.text}</span>
                        </p>
                    ) : isUser ? (
                        <p className="whitespace-pre-wrap break-words leading-relaxed">
                            {message.text}
                        </p>
                    ) : (
                        <CopilotMarkdown content={message.text} />
                    )}

                    {/* An org-wide question answered with the caller's own scope. */}
                    {message.isRestricted && (
                        <p className="mt-2 flex items-start gap-1.5 rounded-md bg-amber-100 px-2 py-1.5 text-[11px] text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                            <Info className="mt-px h-3 w-3 shrink-0" />
                            Scoped to your own records — organisation-wide data needs HR access.
                        </p>
                    )}

                    {!isUser && !message.isError && (
                        <CopilotDataCards data={message.data} compact={compact} />
                    )}

                    {/* Feedback */}
                    {!isUser && !message.isError && onRate && (
                        <div className="mt-2.5 flex items-center gap-2 border-t border-border pt-2">
                            {!compact && (
                                <span className="text-[11px] text-muted-foreground">Helpful?</span>
                            )}
                            <button
                                type="button"
                                onClick={() => onRate(message.id, "up")}
                                title="Helpful"
                                className={cn(
                                    "rounded-md p-1 transition-colors",
                                    feedback === "up"
                                        ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                                        : "text-muted-foreground hover:text-emerald-600"
                                )}
                            >
                                <ThumbsUp className="h-3.5 w-3.5" />
                            </button>
                            <button
                                type="button"
                                onClick={() => onRate(message.id, "down")}
                                title="Not helpful"
                                className={cn(
                                    "rounded-md p-1 transition-colors",
                                    feedback === "down"
                                        ? "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300"
                                        : "text-muted-foreground hover:text-destructive"
                                )}
                            >
                                <ThumbsDown className="h-3.5 w-3.5" />
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <span
                className={cn(
                    "mt-1 text-[10px] text-muted-foreground",
                    compact ? "mx-9" : "mx-11"
                )}
            >
                {message.timestamp}
            </span>
        </div>
    );
};

export default CopilotMessage;
